import { useCallback, useRef, useState } from 'react';
import { GetToken } from '@utils/storage';
import * as config from '@config';

// Mirrors backend internal/services/mozioredirect/request.RedirectCommand.
// Optional flight metadata fields are passed through to Mozio's deeplink.
export interface RedirectRequest {
    date: string;
    time: string;
    retDate?: string;
    retTime?: string;
    pickup: string;
    dropoff: string;
    hours?: string;
    passengerCount: string;
    luggageCount: string;
    airline?: string;
    flightNumber?: string;
    airline2?: string;
    flightNumber2?: string;
    // Optional Mozio-deeplink locale overrides. When omitted, the backend falls
    // back to the agency's stored preference, then to a system default.
    currency?: string;
    language?: string;
}

export type RedirectResult =
    | { kind: 'redirect'; redirectUrl: string; expiresAt: string }
    | { kind: 'fallback'; enquiryId: string; message: string }
    | { kind: 'not_provisioned'; message: string }
    | { kind: 'agency_not_enabled'; message: string }
    | { kind: 'not_configured'; message: string };

interface UseTransferRedirect {
    building: boolean;
    buildRedirect: (req: RedirectRequest) => Promise<RedirectResult>;
    abort: () => void;
}

function useTransferRedirect(): UseTransferRedirect {
    const [building, setBuilding] = useState(false);
    const abortRef = useRef<AbortController | null>(null);

    const buildRedirect = useCallback(async (req: RedirectRequest): Promise<RedirectResult> => {
        setBuilding(true);
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        try {
            const response = await fetch(`${(config as { apiURL2: string }).apiURL2}/transfer-redirects`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${GetToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(req),
                signal: abortRef.current.signal,
            });
            const body = await response.json().catch(() => ({} as Record<string, unknown>));
            if (response.status === 200) {
                if ((body as { fallback?: boolean }).fallback === true) {
                    const b = body as { enquiryId?: string; message?: string };
                    return { kind: 'fallback', enquiryId: b.enquiryId ?? '', message: b.message ?? '' };
                }
                const b = body as { redirectUrl?: string; expiresAt?: string };
                return { kind: 'redirect', redirectUrl: b.redirectUrl ?? '', expiresAt: b.expiresAt ?? '' };
            }
            const b = body as { code?: string; message?: string; error?: string };
            if (response.status === 409 && b.code === 'MOZIO_NOT_PROVISIONED') {
                return { kind: 'not_provisioned', message: b.message ?? '' };
            }
            if (response.status === 409 && b.code === 'AGENCY_NOT_MOZIO_ENABLED') {
                return { kind: 'agency_not_enabled', message: b.message ?? '' };
            }
            if (response.status === 503 && b.code === 'MOZIO_NOT_CONFIGURED') {
                return { kind: 'not_configured', message: b.message ?? 'Mozio is not configured.' };
            }
            throw new Error(b.error ?? b.message ?? `提交失败 (${response.status})`);
        } finally {
            setBuilding(false);
        }
    }, []);

    const abort = useCallback(() => {
        if (abortRef.current) abortRef.current.abort();
    }, []);

    return { building, buildRedirect, abort };
}

export default useTransferRedirect;
